/**
 * Campaign-verb shortcut — intercepts multi-step, iterative user goals
 * ("lead design campaign", "optimize this compound", "hit-to-lead")
 * before the LLM can mis-alias them to single-action handlers.
 *
 * The 2026-04-23 assemble-compound incident is the grounding case:
 * "Let's do a lead design campaign on the current focused compound"
 * routed to assemble-compound and crashed. This shortcut catches those
 * phrases at a dedicated campaign layer and either clarifies, fires a
 * canonical workflow, or responds conversationally — never guesses at
 * a single low-level action.
 *
 * See labwork/studies/gibbs-campaign-verbs/ for full design context.
 *
 * @module gibbs/knowledge/campaignShortcut
 */

/**
 * @typedef {Object} CampaignVerbEntry
 * @property {string} id - Kebab-case identifier, used in clarification context
 * @property {Array<RegExp|string>} phrases - Case-insensitive matchers
 * @property {'clarify'|'workflow'|'chat'} responseClass
 * @property {string} rationale - Why this class; reviewer-facing
 * @property {string[]} [starterActionIds] - Only when responseClass === 'clarify'
 * @property {string} [canonicalPlanBuilder] - Only when responseClass === 'workflow'
 * @property {string} [chatTemplate] - Only when responseClass === 'chat'
 * @property {string} [clarifyQuestion] - Clarify-class only; question shown to user
 * @property {string} [starterLabels] - Clarify-class only; parallel to starterActionIds
 * @property {string} [fallbackChatTemplate] - Clarify/workflow fallback when
 *   precondition filtering removes too many options; 40-800 chars
 */

// Match the existing Docking UI default, but carry it explicitly through the
// reviewed workflow so the browser request and audit trail cannot silently
// depend on a form-only default.
const DEFAULT_CAMPAIGN_DOCK_POSE_COUNT = 6;

// Appended to a mixed-workspace summary. Mirrored in the browser and server
// canonical contracts, which reject any drift between the three copies.
const NATIVE_REFERENCE_SCORING_CLAUSE = ' The native reference is scored with the retained poses and ranked against them.';

/** @type {CampaignVerbEntry[]} */
export const CAMPAIGN_VERB_CATALOG = [
    // ─── 1. Lead design campaign (the 2026-04-23 trigger phrase) ────────────
    {
        id: 'lead-design-campaign',
        phrases: [
            'lead design campaign',
            'design campaign',
            'drug design campaign',
        ],
        responseClass: 'clarify',
        rationale: 'Multi-week iterative goal; no deterministic single-step mapping. User benefits from picking a concrete starting point instead of Gibbs guessing.',
        clarifyQuestion: 'A lead design campaign is a multi-step iterative process. Which concrete step would you like to start with?',
        starterActionIds: [
            'dock-compound',
            'grow-from-all-atoms',
            'job-library-screen',
            'job-reinvent',
        ],
        starterLabels: [
            'Score the focused compound (dock and evaluate)',
            'Grow analogs from its fragment sites',
            'Screen a library for related chemotypes',
            'Generate new compounds with REINVENT',
        ],
        fallbackChatTemplate: 'A lead design campaign is an iterative process: score, grow, screen, generate. To start, load a protein and at least one compound, then ask again. Gibbs can then offer concrete starting points — `dock-compound` to evaluate, `grow-from-all-atoms` to enumerate analogs, or `job-library-screen` to search a library.',
    },

    // ─── 2. Lead optimization ───────────────────────────────────────────────
    {
        id: 'lead-optimization',
        phrases: [
            'lead optimization',
            'optimize this lead',
            'lead opt',
        ],
        responseClass: 'clarify',
        rationale: 'Canonical term with multiple valid starting points (grow analogs, predict ADMET, rescore). Clarify so the user picks the dimension they care about most.',
        clarifyQuestion: 'Lead optimization can start from several angles. Which would you like first?',
        starterActionIds: [
            'grow-from-all-atoms',
            'predict-admet',
            'predict-gnina-scores',
            'job-reinvent',
        ],
        starterLabels: [
            'Grow analogs from the lead',
            'Predict ADMET properties',
            'Rescore with GNINA',
            'Generate analogs with REINVENT',
        ],
        fallbackChatTemplate: 'Lead optimization iterates on a hit to improve potency, selectivity, or ADMET. Load a compound first, then Gibbs can suggest concrete next steps — `grow-from-all-atoms` for analog enumeration, `predict-admet` for property prediction, or `predict-gnina-scores` for rescoring.',
    },

    // ─── 3. Optimize this compound ──────────────────────────────────────────
    {
        id: 'optimize-this-compound',
        phrases: [
            'optimize this compound',
            'optimize the compound',
            'optimize this molecule',
            'optimize the molecule',
        ],
        responseClass: 'clarify',
        rationale: 'Compound-centric version of lead-optimization; same options but phrased as directly targeting the focused compound.',
        clarifyQuestion: 'What kind of optimization? Pick a concrete starting point:',
        starterActionIds: [
            'grow-from-all-atoms',
            'energy-minimize',
            'predict-gnina-scores',
            'predict-admet',
        ],
        starterLabels: [
            'Grow analogs (add/substitute at atoms)',
            'Energy-minimize the pose',
            'Rescore with GNINA',
            'Predict ADMET properties',
        ],
        fallbackChatTemplate: 'To optimize a compound, load it first and make sure a protein is also loaded. Gibbs can then offer concrete moves — `energy-minimize` the pose, `grow-from-all-atoms` to enumerate analogs, or `predict-admet` to assess properties.',
    },

    // ─── 4. Run a campaign ──────────────────────────────────────────────────
    {
        id: 'run-a-campaign',
        phrases: [
            'run a campaign',
            'start a campaign',
            'launch a campaign',
            'begin a campaign',
        ],
        responseClass: 'clarify',
        rationale: 'Generic campaign-level phrasing; maximum ambiguity. Clarify with the widest valid set of starting points.',
        clarifyQuestion: 'A drug-discovery campaign spans many stages. Where would you like to start?',
        starterActionIds: [
            'dock-compound',
            'grow-from-all-atoms',
            'job-library-screen',
            'job-reinvent',
        ],
        starterLabels: [
            'Evaluate an existing compound (dock and score)',
            'Discover hits via fragment growth',
            'Screen a compound library',
            'Generate de novo with REINVENT',
        ],
        fallbackChatTemplate: 'Campaigns are iterative: evaluate, generate, score, filter, repeat. Load a protein and at least one compound to begin; Gibbs can then offer concrete starting actions like `dock-compound`, `grow-from-all-atoms`, or `job-library-screen`.',
    },

    // ─── 5. Hit-to-lead — the only MVP workflow entry ───────────────────────
    {
        id: 'hit-to-lead',
        phrases: [
            'hit-to-lead',
            'hit to lead',
            'h2l',
        ],
        responseClass: 'workflow',
        rationale: 'Industry-standard term with a canonical computational interpretation (evaluate hits, enumerate analogs, rescore). Deterministic mapping justified by widespread usage.',
        canonicalPlanBuilder: 'buildHitToLeadPlan',
        fallbackChatTemplate: 'Hit-to-lead iterates on early hits to produce a lead candidate. Load a protein and at least one compound, then Gibbs can run the canonical pipeline — `dock-compound` to evaluate, `grow-from-all-atoms` to enumerate analogs, and `predict-gnina-scores` to rank.',
    },

    // ─── 6. Generate and triage analogs ─────────────────────────────────────
    {
        id: 'generate-and-triage-analogs',
        phrases: [
            'generate and triage analogs',
            'generate analogs',
            'make analogs',
            'create analogs',
            'produce analogs',
        ],
        responseClass: 'clarify',
        rationale: 'Two-phase ask (generate + triage) but user may care about one or the other first. Clarify which phase to start.',
        clarifyQuestion: 'Analog generation has two phases — creating candidates and then filtering/ranking them. Which would you like first?',
        starterActionIds: [
            'grow-from-all-atoms',
            'job-reinvent',
            'predict-gnina-scores',
            'predict-admet',
        ],
        starterLabels: [
            'Enumerate analogs via fragment growth',
            'Generate with REINVENT (AI)',
            'Rescore existing analogs with GNINA',
            'Filter existing analogs by ADMET',
        ],
        fallbackChatTemplate: 'Analog generation combines enumeration (`grow-from-all-atoms` or `job-reinvent`) with triage (`predict-gnina-scores`, `predict-admet`). Load a starting compound and protein, then Gibbs can offer concrete starting points.',
    },

    // ─── 7. Design new compounds ────────────────────────────────────────────
    {
        id: 'design-new-compounds',
        phrases: [
            'design new compounds',
            'design new molecules',
            'design some compounds',
            'design some molecules',
        ],
        responseClass: 'clarify',
        rationale: 'Generic design request. "Design" implies creation, not evaluation — starters bias toward generation methods.',
        clarifyQuestion: 'How would you like to design the new compounds?',
        starterActionIds: [
            'grow-from-all-atoms',
            'job-reinvent',
            'job-library-screen',
            'dock-compound',
        ],
        starterLabels: [
            'Grow from fragment attachment points',
            'Generate de novo with REINVENT',
            'Screen a library for candidates',
            'Evaluate a specific starting compound first',
        ],
        fallbackChatTemplate: 'Designing new compounds can mean enumeration (`grow-from-all-atoms`), AI generation (`job-reinvent`), or library screening (`job-library-screen`). Load a protein and optionally a seed compound to start.',
    },

    // ─── 8. Improve this compound (chat) ────────────────────────────────────
    {
        id: 'improve-this-compound',
        phrases: [
            'improve this compound',
            'improve the compound',
            'improve this molecule',
            'improve the molecule',
        ],
        responseClass: 'chat',
        rationale: 'Vague enough that a 3–5 option clarification would feel arbitrary — user wants narrative guidance, not a menu.',
        chatTemplate: 'Improving a compound is iterative — you pick a dimension (potency, selectivity, ADMET, synthesizability) and make moves. Concrete starting actions in Gibbs: `grow-from-all-atoms` to enumerate analogs at specific atoms, `predict-admet` to check properties, `predict-gnina-scores` to rescore after changes, or `energy-minimize` to relax a pose. Tell me which dimension matters most and I can propose a path.',
    },

    // ─── 9. Make this better (chat) ─────────────────────────────────────────
    {
        id: 'make-this-better',
        phrases: [
            'make this better',
            'make it better',
            'make this compound better',
        ],
        responseClass: 'chat',
        rationale: 'Maximally vague; clarification options would be guesses. A conversational nudge toward concrete goals is more useful than a menu.',
        chatTemplate: '"Better" in drug discovery usually means one of: stronger binding, cleaner selectivity, or improved ADMET. Gibbs can help with each — `predict-gnina-scores` to check binding, `predict-admet` for properties, `grow-from-all-atoms` to enumerate modifications, or `energy-minimize` to tighten a pose. Which dimension are you trying to improve?',
    },

    // ─── 10. Iterate on this hit (chat) ─────────────────────────────────────
    {
        id: 'iterate-on-this-hit',
        phrases: [
            'iterate on this hit',
            'iterate on the hit',
            'iterate this hit',
        ],
        responseClass: 'chat',
        rationale: 'Iteration implies a loop, which Gibbs cannot execute autonomously today. Chat response explains the loop shape and suggests first-round actions.',
        chatTemplate: 'Iterating on a hit is a loop: modify → score → triage → modify. Gibbs can run each step — `grow-from-all-atoms` to enumerate modifications, `dock-compound` or `predict-gnina-scores` to score, `predict-admet` to triage on properties. The loop itself is driven by you choosing which round to run next. Want to start a first round?',
    },
];

/**
 * Match a user message against the campaign catalog.
 *
 * @param {string} userMessage
 * @returns {CampaignVerbEntry|null}
 */
export function matchCampaignIntent(userMessage) {
    if (!userMessage || typeof userMessage !== 'string') return null;
    const lower = userMessage.toLowerCase().trim();
    if (!lower) return null;
    for (const entry of CAMPAIGN_VERB_CATALOG) {
        for (const pattern of entry.phrases) {
            if (pattern instanceof RegExp) {
                if (pattern.test(lower)) return entry;
            } else if (typeof pattern === 'string') {
                if (lower.includes(pattern)) return entry;
            }
        }
    }
    return null;
}

// ─── Intent builders ────────────────────────────────────────────────────────

/**
 * Minimum options required on a campaign clarify intent. Below this
 * threshold the UX becomes a binary yes/no (or single option) which is
 * not useful — downgrade to the fallback chat template instead.
 */
const MIN_CLARIFY_OPTIONS = 3;

/**
 * Build a clarify intent from a catalog entry, filtering starter options
 * by whether their preconditions are met in the current workspace.
 *
 * If filtering drops the option count below MIN_CLARIFY_OPTIONS, this
 * returns null so the caller can downgrade to the entry's fallback.
 *
 * @param {CampaignVerbEntry} entry
 * @param {string} userMessage
 * @param {Object} workspaceState
 * @param {(actionId: string, ws: Object) => {met: boolean}} preconditionChecker
 * @returns {Object|null}
 */
function buildClarifyIntent(entry, userMessage, workspaceState, preconditionChecker) {
    const ids = entry.starterActionIds || [];
    const labels = entry.starterLabels || [];
    const options = [];
    for (let i = 0; i < ids.length; i++) {
        const actionId = ids[i];
        const { met } = preconditionChecker(actionId, workspaceState);
        if (!met) continue;
        options.push({
            label: labels[i] || actionId,
            value: actionId,
        });
    }
    if (options.length < MIN_CLARIFY_OPTIONS) return null;
    return {
        intent: 'clarify',
        question: entry.clarifyQuestion || 'Which step would you like to start with?',
        options,
        missing: [],
        context: {
            cascade: 'campaign_verb',
            catalogEntryId: entry.id,
            originalMessage: userMessage,
        },
    };
}

/**
 * Build a chat intent from a catalog entry's chatTemplate.
 *
 * @param {CampaignVerbEntry} entry
 * @returns {Object|null}
 */
function buildChatIntent(entry) {
    const message = entry.chatTemplate || entry.fallbackChatTemplate;
    if (!message) return null;
    return { intent: 'chat', message };
}

/**
 * Canonical hit-to-lead plan builder. Builds a dock → minimize → score
 * chain against currently-loaded compounds; returns null when the
 * workspace does not meet the plan's preconditions (caller downgrades
 * to the entry's fallback chat template).
 *
 * @param {Object} workspaceState
 * @param {(actionId: string, ws: Object) => {met: boolean}} preconditionChecker
 * @returns {Object|null}
 */
function buildHitToLeadPlan(workspaceState, preconditionChecker, opts = {}) {
    const campaignEnabled = opts.autonomousCampaignEnabled === true;
    const needed = campaignEnabled
        ? ['dock-compound', 'get-energies', 'energy-minimize', 'predict-admet', 'export-compounds']
        : ['dock-compound', 'energy-minimize', 'predict-gnina-scores'];
    for (const id of needed) {
        const { met } = preconditionChecker(id, workspaceState);
        if (!met) return null;
    }
    if (campaignEnabled) {
        // Product callers pass the live App.Workspace so registry preconditions
        // can use its methods. Fragment availability, however, belongs to the
        // masked serialized projection shared with the Deputy/Orchestrator.
        // Prefer that projection when supplied so plan generation and workflow
        // submission cannot disagree about whether a grow step is required.
        const fragmentState = opts.workspaceSnapshot || workspaceState;
        const fragmentStatus = fragmentState?.fragmentAvailability?.status;
        const canGrow = fragmentStatus
            ? fragmentStatus === 'available'
            : (fragmentState?.fragInfoCount || 0) > 0;
        const snapshotCompounds = Array.isArray(fragmentState?.compounds)
            ? fragmentState.compounds
            : [];
        const nativeBinderCount = snapshotCompounds.filter(
            (compound) => compound?.isNativeBinder === true,
        ).length;
        const allNativeReferences = snapshotCompounds.length > 0
            && nativeBinderCount === snapshotCompounds.length;
        const nativeOnly = allNativeReferences;
        const candidateCompounds = snapshotCompounds.filter(
            (compound) => compound?.isNativeBinder !== true,
        );
        const undockedCompounds = candidateCompounds.filter(
            (compound) => compound?.isDockedPose !== true,
        );
        // An omitted compound projection is unknown state, not proof that every
        // current pose is already docked. Fail closed into the dock/minimize
        // path; submission still rejects an empty signed parent baseline.
        const hasUndockedCandidates = candidateCompounds.length === 0
            || undockedCompounds.length > 0;
        // A native reference in a workspace that also holds candidates must be
        // scored in the same step as the retained poses, so the ranked table
        // compares candidates against a live crystal-ligand score rather than a
        // persisted one. Growth and minimization still exclude it.
        const scoresNativeReference = !nativeOnly && nativeBinderCount > 0;
        const poseScoreRef = scoresNativeReference
            ? 'native-and-docked-poses'
            : 'docked-poses';
        let summary;
        if (nativeOnly && canGrow) {
            summary = [
                'Native-reference hit-to-lead campaign: score the native binder, dock new poses,',
                'minimize and re-score the poses, grow plausible scored structures, minimize and',
                're-score new growth products, then predict ADMET, rank, and export ligand identities.',
            ].join(' ');
        } else if (nativeOnly) {
            summary = [
                'Native-reference hit-to-lead campaign: score the native binder, dock new poses,',
                'and minimize and re-score the poses. Fragment growth is not authorized by current',
                'workspace evidence; continue safe ADMET, ranking, and export for the native identity.',
            ].join(' ');
        } else if (canGrow && hasUndockedCandidates) {
            summary = 'Hit-to-lead campaign: dock eligible undocked compounds, minimize every docked pose, score and filter them, grow, minimize the new products, re-score them, predict ADMET, and export.';
        } else if (canGrow) {
            summary = 'Hit-to-lead campaign: minimize every docked pose, score and filter them, grow, minimize the new products, re-score them, predict ADMET, and export.';
        } else if (hasUndockedCandidates) {
            summary = [
                'Hit-to-lead campaign: dock eligible undocked compounds, minimize every docked pose,',
                'score and filter them, predict ADMET, and export.',
                'Fragment growth is unavailable in this workspace.',
            ].join(' ');
        } else {
            summary = [
                'Hit-to-lead campaign: minimize every docked pose, score and filter them, predict ADMET,',
                'and export. Fragment growth is unavailable in this workspace.',
            ].join(' ');
        }
        if (scoresNativeReference) summary += NATIVE_REFERENCE_SCORING_CLAUSE;
        // Pre-growth minimize/score is unconditional: every non-native
        // candidate pose is filtered before growth, whether it is a successor
        // of this campaign's live dock or a pose already docked in the loaded
        // workspace. Only the dock step itself is state-dependent, so any saved
        // file follows the same contract regardless of how its poses arose.
        const standardActions = [
            ...(hasUndockedCandidates ? [{
                actionId: 'dock-compound',
                params: {
                    dockingProgram: 'autodock',
                    speedArg: 'accurate',
                    poseCount: DEFAULT_CAMPAIGN_DOCK_POSE_COUNT,
                },
                resolvedInputs: { compounds: 'undocked' },
            }] : []),
            {
                actionId: 'energy-minimize',
                params: { minimizationType: 'Rigid' },
                resolvedInputs: { compounds: 'docked-poses', caseData: 'current' },
            },
            {
                actionId: 'get-energies',
                params: {},
                resolvedInputs: { compounds: poseScoreRef },
            },
            ...(canGrow ? [{
                actionId: 'grow-from-atom',
                params: { growMethodology: 'fragment-best-pose' },
                resolvedInputs: {},
            }] : []),
            ...(canGrow ? [{
                actionId: 'energy-minimize',
                params: { minimizationType: 'Rigid' },
                resolvedInputs: { compounds: 'grown', caseData: 'current' },
            }, {
                actionId: 'get-energies',
                params: {},
                resolvedInputs: { compounds: 'grown' },
            }] : []),
            {
                actionId: 'predict-admet',
                params: {},
                resolvedInputs: { compounds: 'all' },
            },
            {
                actionId: 'export-compounds',
                params: { format: 'sdf' },
                resolvedInputs: { compounds: 'all' },
            },
        ].map((action, index) => ({ ...action, step: index + 1 }));
        const nativeOnlyActions = [
            {
                actionId: 'get-energies',
                params: {},
                resolvedInputs: { compounds: 'all' },
                step: 1,
            },
            {
                actionId: 'dock-compound',
                params: {
                    dockingProgram: 'autodock',
                    speedArg: 'accurate',
                    poseCount: DEFAULT_CAMPAIGN_DOCK_POSE_COUNT,
                },
                resolvedInputs: { compounds: 'all' },
                step: 2,
            },
            {
                actionId: 'energy-minimize',
                params: { minimizationType: 'Rigid' },
                resolvedInputs: { compounds: 'all', caseData: 'current' },
                step: 3,
            },
            {
                actionId: 'get-energies',
                params: {},
                resolvedInputs: { compounds: 'all' },
                step: 4,
            },
            ...(canGrow ? [{
                actionId: 'grow-from-atom',
                params: { growMethodology: 'fragment-best-pose' },
                resolvedInputs: {},
                step: 5,
            }, {
                actionId: 'energy-minimize',
                params: { minimizationType: 'Rigid' },
                resolvedInputs: { compounds: 'all', caseData: 'current' },
                step: 6,
            }, {
                actionId: 'get-energies',
                params: {},
                resolvedInputs: { compounds: 'all' },
                step: 7,
            }] : []),
            {
                actionId: 'predict-admet',
                params: {},
                resolvedInputs: { compounds: 'all' },
                step: canGrow ? 8 : 5,
            },
            {
                actionId: 'export-compounds',
                params: { format: 'sdf' },
                resolvedInputs: { compounds: 'all' },
                step: canGrow ? 9 : 6,
            },
        ];
        return {
            intent: 'workflow',
            executionMode: 'campaign',
            workflowId: 'hit-to-lead',
            source: 'campaign-verb-shortcut',
            summary,
            actions: nativeOnly ? nativeOnlyActions : standardActions,
        };
    }
    return {
        intent: 'workflow',
        summary: 'Hit-to-lead: dock loaded compounds, minimize poses, rescore with GNINA.',
        actions: [
            {
                actionId: 'dock-compound',
                params: { dockingProgram: 'autodock', speedArg: 'accurate' },
                resolvedInputs: { compounds: 'all', caseData: 'current' },
                step: 1,
            },
            {
                actionId: 'energy-minimize',
                params: { minimizationType: 'Rigid' },
                resolvedInputs: { compounds: 'all', caseData: 'current' },
                step: 2,
            },
            {
                actionId: 'predict-gnina-scores',
                params: { skipDocking: true },
                resolvedInputs: { compounds: 'all' },
                step: 3,
            },
        ],
    };
}

/** @type {Record<string, Function>} */
const PLAN_BUILDERS = {
    buildHitToLeadPlan,
};

/**
 * Default precondition checker. Accepts the optional injection point
 * used by tests to avoid importing the registry in mock setups. When
 * not injected, the shortcut imports and uses the real registry check.
 *
 * Lazy-required to avoid circular imports at module load time and to
 * keep tryCampaignShortcut itself side-effect-free when no catalog
 * match is found.
 */
function defaultPreconditionChecker(actionId, workspaceState) {
    // eslint-disable-next-line global-require
    const { checkPreconditions } = require('../registry/registry');
    return checkPreconditions(actionId, workspaceState);
}

/**
 * Intercept campaign-level verbs before the LLM routing step.
 *
 * @param {string} userMessage - Raw user input
 * @param {Object} workspaceState - Workspace snapshot or live workspace
 * @param {Object} [opts] - Test-only overrides
 * @param {Function} [opts.preconditionChecker] - Inject a stub checker
 * @returns {Object|null} classified intent or null if no catalog match
 */
export function tryCampaignShortcut(userMessage, workspaceState, opts = {}) {
    try {
        const entry = matchCampaignIntent(userMessage);
        if (!entry) return null;
        const checker = opts.preconditionChecker || defaultPreconditionChecker;

        if (entry.responseClass === 'workflow') {
            const builderFn = PLAN_BUILDERS[entry.canonicalPlanBuilder];
            if (!builderFn) {
                return buildChatIntent(entry);
            }
            const plan = builderFn(workspaceState, checker, opts);
            if (plan) return plan;
            return buildChatIntent(entry);
        }

        if (entry.responseClass === 'clarify') {
            const clarify = buildClarifyIntent(
                entry, userMessage, workspaceState, checker
            );
            if (clarify) return clarify;
            return buildChatIntent(entry);
        }

        if (entry.responseClass === 'chat') {
            return buildChatIntent(entry);
        }

        return null;
    } catch (_) {
        // Shortcut never throws — swallow and pass through to LLM.
        return null;
    }
}
